/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Tag16Regular } from '@fluentui/react-icons';
import { ActionButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { FontIcon } from '@fluentui/react/lib/Icon';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { SublistSynonymsCell } from 'src/luis/verticals/entityDetails/components/listEntityDetails/SublistSynonymsCell';
import { useQnaMetricActions } from 'src/qnaMaker/core/hooks/useQnaMetricActions';
import { QnaMakerMetadataType, QnaMakerQnaSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';

type QnaMakerQnaPairMetadataSectionProps = {
    metadata: QnaMakerMetadataType;
    handleSubmitMetadata: (newData: Partial<QnaMakerQnaSwagger>) => void;
    handleEditMetadata: (buttonPlace: string, section: string, action: string) => void;
};

const messages = defineMessages({
    metadata: {
        id: 'qnaMaker.qnaMakerQnaPairMetadataSection.metadata',
        defaultMessage: 'Metadata'
    },
    description: {
        id: 'qnaMaker.qnaMakerQnaPairMetadataSection.description',
        defaultMessage:
            'Metadata tags are used to let the client application narrow down the results of a user query. For example, "where is parking located" can have a different answer if the location of the restaurant branch is different - that is, the metadata is Location: Seattle versus Location: Redmond.'
    },
    addMetadata: {
        id: 'qnaMaker.qnaMakerQnaPairMetadataSection.addMetadata',
        defaultMessage: 'Add metadata'
    },
    key: {
        id: 'qnaMaker.qnaMakerQnaPairAlternateQuestionsSection.key',
        defaultMessage: 'Key:'
    },
    value: {
        id: 'qnaMaker.qnaMakerQnaPairAlternateQuestionsSection.value',
        defaultMessage: 'Value:'
    },
    keyPlaceholder: {
        id: 'qnaMaker.qnaMakerQnaPairAlternateQuestionsSection.keyPlaceholder',
        defaultMessage: 'Add key and press tab'
    },
    valuePlaceholder: {
        id: 'qnaMaker.qnaMakerQnaPairAlternateQuestionsSection.valuePlaceholder',
        defaultMessage: 'Add value and press enter'
    },
    expand: {
        id: 'qnaMaker.qnaMakerQnaPairAlternateQuestionsSection.expand',
        defaultMessage: 'Expand Metadata collapse button'
    },
    collapse: {
        id: 'qnaMaker.qnaMakerQnaPairAlternateQuestionsSection.collapse',
        defaultMessage: 'Collapse Metadata collapse button'
    }
});

const theme = getScopedTheme('QnaMakerQnaPairDetails');

const addButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    label: { color: theme.buttonColor },
    labelDisabled: { color: theme.disabledLabelColor }
});

export const QnaMakerQnaPairMetadataSection = observer((props: QnaMakerQnaPairMetadataSectionProps) => {
    const { metadata, handleEditMetadata, handleSubmitMetadata } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const { increaseNumberOfClicks } = useQnaMetricActions();

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const { languageDirection } = rootStore.currentQnaMakerProjectStore;

    const [isMetadataExpanded, setIsMetadataExpanded] = React.useState(false);
    const [isInAddMode, setIsInAddMode] = React.useState(false);

    const [editableKey, setEditableKey] = React.useState('');
    const [editableValue, setEditableValue] = React.useState('');

    const onSubmitNewMetadata = () => {
        if (editableKey.length > 0 && editableValue.length > 0) {
            handleSubmitMetadata({ metadata: { ...metadata, [editableKey]: editableValue } });
        }
        setIsInAddMode(false);
        setEditableKey('');
        setEditableValue('');
    };

    const handleKeyUp = (e: React.KeyboardEvent<HTMLElement | HTMLTextAreaElement>) => {
        if (e.key === LuisKeyCodes.Enter) {
            onSubmitNewMetadata();
        }

        if (e.key === LuisKeyCodes.Escape) {
            setEditableKey('');
            setEditableValue('');
            setIsInAddMode(false);
        }
    };

    return (
        <Stack tokens={{ childrenGap: 10 }}>
            <Stack horizontal tokens={{ childrenGap: 5 }}>
                <FontIcon
                    tabIndex={0}
                    aria-hidden={false}
                    onClick={() => setIsMetadataExpanded(!isMetadataExpanded)}
                    onKeyDown={e => {
                        if (e.key === 'Enter') {
                            setIsMetadataExpanded(!isMetadataExpanded);
                        }
                    }}
                    iconName={isMetadataExpanded ? 'ChevronDown' : 'ChevronRight'}
                    style={{ cursor: 'pointer' }}
                    aria-label={isMetadataExpanded ? localize(messages.collapse) : localize(messages.expand)}
                />
                <Tag16Regular />
                <LuisText fontWeight={600}>{localize(messages.metadata)}</LuisText>
                <LuisText fontWeight={600}>({Object.keys(metadata || []).length})</LuisText>
            </Stack>
            {isMetadataExpanded && (
                <Stack tokens={{ childrenGap: 5 }}>
                    <LuisText variant="smallPlus" color={theme.descriptionColor}>
                        {localize(messages.description)}
                    </LuisText>
                    <RBACTooltip
                        hidden={canWrite}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="addMetadataButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <ActionButton
                                disabled={!canWrite || isInAddMode}
                                styles={addButtonStyles}
                                iconProps={{ iconName: 'Add' }}
                                text={localize(messages.addMetadata)}
                                onClick={() => {
                                    setIsInAddMode(true);
                                    increaseNumberOfClicks();
                                    handleEditMetadata('Metadata', 'Button', 'Add');
                                }}
                            />
                        }
                    />
                    <SublistSynonymsCell
                        isEditMode={false}
                        isRemovingDisabled={false}
                        values={Object.entries(metadata).map(e => `${e[0]}: ${e[1]}`)}
                        onAddValues={undefined}
                        onRemoveValues={values =>
                            handleSubmitMetadata({
                                metadata: Object.fromEntries(
                                    Object.entries(metadata).filter(([key]) => values.some(v => v.split(':')[0] !== key))
                                )
                            })
                        }
                    />
                    {isInAddMode && (
                        <Stack horizontal tokens={{ childrenGap: 15 }}>
                            <Stack tokens={{ childrenGap: 5 }}>
                                <LuisText>{localize(messages.key)}</LuisText>
                                <LuisTextField
                                    required
                                    autoFocus
                                    value={editableKey}
                                    dir={languageDirection}
                                    onChange={(_, v) => setEditableKey(v)}
                                    placeholder={localize(messages.keyPlaceholder)}
                                />
                            </Stack>
                            <LuisText styles={{ root: { display: 'flex', paddingTop: '20px' } }}>:</LuisText>
                            <Stack tokens={{ childrenGap: 5 }}>
                                <LuisText>{localize(messages.value)}</LuisText>
                                <LuisTextField
                                    required
                                    value={editableValue}
                                    onKeyUp={handleKeyUp}
                                    dir={languageDirection}
                                    onBlur={onSubmitNewMetadata}
                                    onChange={(_, v) => setEditableValue(v)}
                                    placeholder={localize(messages.valuePlaceholder)}
                                />
                            </Stack>
                        </Stack>
                    )}
                </Stack>
            )}
        </Stack>
    );
});
